import { TellescopeModelFromName, useTellescopeQuery, useUsersMap } from "@tellescope/tanstack-query";
import { formatDateTime, formatName } from "@tellescope/utils";
import { renderAnswer } from "./render-answer";
import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

/*
 * NOTE: this has hard-coded color values for now.
 * We need to ensure tailwind styles are available to the print flow
 */

type Response = TellescopeModelFromName<"form_responses">;

export interface FormResponsePrintablePDFProps {
  submittedAt: string | Date;
  response: Response;
  enduserName: string;
  logoDataUrl?: string; // Optional pre-fetched logo as data URL
  hideLogo?: boolean;
}

export const FormResponsePrintablePDF = forwardRef<HTMLDivElement, FormResponsePrintablePDFProps>(
  ({ response, enduserName, submittedAt, logoDataUrl, hideLogo = false }, ref) => {
    const { t } = useTranslation("forms");
    const { data: { theme } = {} } = useTellescopeQuery({
      model: "organizations",
      method: "get_theme",
      request: {
        businessId: response.businessId,
      },
    });

    const users = useUsersMap();
    const submitterName = useMemo(() => {
      if (response.enduserId === response.submittedBy) {
        return enduserName;
      }
      if (response.submittedBy) {
        const user = users[response.submittedBy];
        return user ? formatName(user) : undefined;
      }

      return undefined;
    }, [enduserName, response.enduserId, response.submittedBy, users]);

    return (
      <div style={{ padding: "24px 16px" }} ref={ref}>
        {/* Show logoDataUrl for PDF generation (CORS-safe), or theme.logoURL for browser print */}
        {!hideLogo && (logoDataUrl || theme?.logoURL) && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
            <img
              src={logoDataUrl || theme?.logoURL}
              alt={t("Organization Logo")}
              style={{ height: "64px", maxWidth: "200px", objectFit: "contain" }}
            />
          </div>
        )}
        <div
          style={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "24px",
          }}
        >
          <div
            style={{
              textAlign: "center",
              paddingBottom: "16px",
              borderBottom: "1px solid #e5e7eb",
              marginBottom: "16px",
            }}
          >
            <h3 style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: 0 }}>
              {response.formTitle || t("Form Response")}
            </h3>
            <p style={{ fontSize: "14px", color: "#4b5563", marginTop: "4px", marginBottom: 0 }}>{t("PDF Preview")}</p>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <span style={{ fontSize: "14px", fontWeight: 500, color: "#374151" }}>{t("Patient Name:")}</span>
              <span style={{ fontSize: "14px", color: "#111827" }}>{enduserName}</span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
              }}
            >
              <span style={{ fontSize: "14px", fontWeight: 500, color: "#374151" }}>{t("Submitted By:")}</span>
              <span style={{ fontSize: "14px", color: "#111827" }}>{submitterName || t("Unknown")}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "14px", fontWeight: 500, color: "#374151" }}>{t("Date & Time:")}</span>
              <span style={{ fontSize: "14px", color: "#111827" }}>{formatDateTime(submittedAt)}</span>
            </div>
          </div>
          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: "16px" }}>
            <h4 style={{ fontSize: "18px", fontWeight: 500, color: "#111827", marginBottom: "12px" }}>
              {t("Form Responses")}
            </h4>
            <div>
              {response.responses?.map((item) => (
                <div key={item.fieldId} style={{ marginBottom: "16px", pageBreakInside: "avoid" }}>
                  <div style={{ fontSize: "14px", fontWeight: 500, color: "#374151", marginBottom: "8px" }}>
                    {item.fieldTitle}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#4b5563",
                      backgroundColor: "#f9fafb",
                      padding: "12px",
                      borderRadius: "4px",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    {renderAnswer(item.answer)}
                  </div>
                </div>
              ))}
              {response.responses?.length === 0 && (
                <div style={{ textAlign: "center", padding: "32px 0", color: "#6b7280" }}>
                  {t("No response data available.")}
                </div>
              )}
            </div>
          </div>
        </div>
        <style>{`
        @media print {
          @page {
            margin: 1cm;
          }

          /* Force background colors and borders to print */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
        }
      `}</style>
      </div>
    );
  }
);
